import type { PriceQuote } from '@ducat-unit/core';
import { is_oracle_event_fresh } from '@ducat-unit/core/lib';
import { price } from '@ducat-unit/core/schema';

// Match PriceOracleClient.fetch.quotes()'s default freshness window.
export const MAX_QUOTE_AGE_SECONDS = 5 * 60;
// Display and signing both reject future-dated quotes; there is no clock-skew allowance.
export type QuoteContext = { oraclePubkey: string; network: string };

// Compare app profiles to chain identity without rewriting signed payload labels.
function canonicalNetwork(network: string): string {
  return network === 'mainnet-alpha' || network === 'alpha-mainnet' ? 'main' : network;
}

export function matchesOracleContext(value: Pick<PriceQuote, 'oracle_pubkey' | 'chain_network'>, context: QuoteContext): boolean {
  return value.oracle_pubkey === context.oraclePubkey && canonicalNetwork(value.chain_network) === canonicalNetwork(context.network);
}

export function parseUsableOracleQuote(value: unknown, context: QuoteContext, now = Math.floor(Date.now() / 1000)): PriceQuote | undefined {
  const parsed = price.quote.safeParse(value);
  if (!parsed.success) return;
  const quote = parsed.data;
  if (
    quote.base_price <= 0 ||
    !matchesOracleContext(quote, context) ||
    quote.base_stamp > now ||
    !is_oracle_event_fresh(quote, MAX_QUOTE_AGE_SECONDS, now)
  ) {
    return;
  }
  return quote as PriceQuote;
}
